// frontend/src/api/batch.ts
import { apiClient } from './client';
import type { Plan } from './plans';

export interface Batch {
  id: string;
  course_id: string;
  name: string;
  description: string | null;
  start_date: string | null;
  end_date: string | null;
  is_active: boolean;
  is_default: boolean;
  created_at: string;
}

export interface BatchWithPlans {
  id: string;
  name: string;
  description: string | null;
  is_default: boolean;
  course_id: string;
  course_name: string;
  plans: Plan[];
}

export type PlanStatusValue =
  | 'active'
  | 'requires_plan'
  | 'expired'
  | 'denied'
  | 'no_plan_required';

export interface EnrollmentStatus {
  enrolled: boolean;
  batch_id: string | null;
  plan_status: PlanStatusValue | null;
  available_batches: BatchWithPlans[];
}

export interface BatchMember {
  id: string;
  batch_id: string;
  user_id: string;
  user_display_name: string | null;
  user_email: string | null;
  status: string;
  joined_at: string;
}

export interface BatchQuiz {
  id: string;
  batch_id: string;
  assessment_id: string;
  assessment_title: string | null;
  assigned_at: string;
}

export interface CreateBatchRequest {
  name: string;
  description?: string;
  start_date?: string;
  end_date?: string;
  is_default?: boolean;
}

export interface UpdateBatchRequest {
  name?: string;
  description?: string;
  start_date?: string;
  end_date?: string;
  is_active?: boolean;
  is_default?: boolean;
}

export async function listBatches(courseId: string): Promise<Batch[]> {
  return apiClient.get<Batch[]>(`/organizer/courses/${courseId}/batches`);
}

export async function createBatch(courseId: string, data: CreateBatchRequest): Promise<Batch> {
  return apiClient.post<Batch>(`/organizer/courses/${courseId}/batches`, data);
}

export async function getBatch(id: string): Promise<Batch> {
  return apiClient.get<Batch>(`/organizer/batches/${id}`);
}

export async function updateBatch(id: string, data: UpdateBatchRequest): Promise<Batch> {
  return apiClient.put<Batch>(`/organizer/batches/${id}`, data);
}

export async function deleteBatch(id: string): Promise<void> {
  return apiClient.delete(`/organizer/batches/${id}`);
}

export async function listBatchMembers(batchId: string): Promise<BatchMember[]> {
  return apiClient.get<BatchMember[]>(`/organizer/batches/${batchId}/members`);
}

export async function addBatchMember(batchId: string, userId: string): Promise<BatchMember> {
  return apiClient.post<BatchMember>(`/organizer/batches/${batchId}/members`, { user_id: userId });
}

export async function removeBatchMember(batchId: string, userId: string): Promise<void> {
  return apiClient.delete(`/organizer/batches/${batchId}/members/${userId}`);
}

export async function listBatchQuizzes(batchId: string): Promise<BatchQuiz[]> {
  return apiClient.get<BatchQuiz[]>(`/organizer/batches/${batchId}/assessments`);
}

export async function assignQuizToBatch(batchId: string, quizId: string): Promise<BatchQuiz> {
  return apiClient.post<BatchQuiz>(`/organizer/batches/${batchId}/assessments`, { assessment_id: quizId });
}

export async function unassignQuizFromBatch(batchId: string, quizId: string): Promise<void> {
  return apiClient.delete(`/organizer/batches/${batchId}/assessments/${quizId}`);
}

// ── Student enrollment ────────────────────────────────────────────────────

/** Self-enroll in a batch. Also auto-activates the default free plan if configured. */
export async function studentEnrollInBatch(
  batchId: string,
): Promise<{ enrolled: boolean; batch_id: string }> {
  return apiClient.post(`/student/batches/${batchId}/enroll`, {});
}

/** Get enrollment + plan status for a quiz. Used before entering the quiz player. */
export async function getEnrollmentStatus(quizId: string): Promise<EnrollmentStatus> {
  return apiClient.get(`/student/assessments/${quizId}/enrollment-status`);
}
